import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { AlertCircle } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../ui/dialog";
import { Button } from "../ui/button";
import { TextField, FormField } from "../shared/FormField";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../ui/select";
import { Skeleton } from "../ui/skeleton";
import { apiClient, getApiErrorMessage } from "../../lib/apiClient";
import { todayLocal as today } from "../../lib/utils";

export function GenerateDialog({ open, onOpenChange, onDone }) {
  const [date, setDate] = useState(today());
  const [type, setType] = useState("2");
  const [state, setState] = useState({ loading: true, error: null, preview: null });
  const [generating, setGenerating] = useState(false);

  const loadPreview = useCallback(async () => {
    setState((s) => ({ ...s, loading: true, error: null }));
    try {
      const { data } = await apiClient.get("/deliveries/generate/candidates", { params: { date, type } });
      setState({ loading: false, error: null, preview: data });
    } catch (error) {
      setState({ loading: false, error: getApiErrorMessage(error, "Could not load generation candidates."), preview: null });
    }
  }, [date, type]);

  useEffect(() => {
    if (open) loadPreview();
  }, [open, loadPreview]);

  const handleConfirm = async () => {
    setGenerating(true);
    try {
      const { data } = await apiClient.post("/deliveries/generate", null, { params: { date, type } });
      toast.success(`Generated ${data.created} deliveries for ${date} (${data.skipped_existing} already existed)`);
      onDone(date);
    } catch (error) {
      toast.error(getApiErrorMessage(error, "Could not generate deliveries."));
    } finally {
      setGenerating(false);
    }
  };

  const p = state.preview;
  const visibleCandidates = p?.candidates?.slice(0, 50) || [];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="generate-deliveries-dialog">
        <DialogHeader>
          <DialogTitle>Generate Deliveries</DialogTitle>
          <DialogDescription>
            Creates a pending delivery row for every eligible subscription on the chosen date. Subscriptions that already have a delivery for that date are skipped automatically.
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-2 gap-4">
          <TextField label="Date" required type="date" value={date} onChange={(e) => setDate(e.target.value)} testId="generate-dialog-date" />
          <FormField label="Type" testId="generate-dialog-type">
            <Select value={type} onValueChange={setType}>
              <SelectTrigger data-testid="generate-dialog-type-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="1">Pickup</SelectItem>
                <SelectItem value="2">Delivery</SelectItem>
              </SelectContent>
            </Select>
          </FormField>
        </div>

        {state.loading ? (
          <Skeleton className="h-40 w-full" />
        ) : state.error ? (
          <p className="text-sm text-danger" data-testid="generate-dialog-error">{state.error}</p>
        ) : (
          <>
            <div className="grid grid-cols-3 gap-3 rounded-md border border-accent/40 bg-accent/10 p-4 text-center">
              <div>
                <p className="text-xs text-text-muted">Eligible</p>
                <p className="font-heading text-xl font-semibold text-text-main" data-testid="generate-dialog-total">{p.total}</p>
              </div>
              <div>
                <p className="text-xs text-text-muted">Already Scheduled</p>
                <p className="font-heading text-xl font-semibold text-text-main" data-testid="generate-dialog-already-scheduled">{p.already_scheduled}</p>
              </div>
              <div>
                <p className="text-xs text-text-muted">Will Create</p>
                <p className="font-heading text-xl font-semibold text-primary" data-testid="generate-dialog-will-create">{p.will_create}</p>
              </div>
            </div>

            {p.will_create === 0 ? (
              <div className="flex items-center gap-2 rounded-md border border-border bg-background p-3 text-sm text-text-muted" data-testid="generate-dialog-nothing-to-create">
                <AlertCircle size={16} strokeWidth={1.5} /> Nothing to generate for {date} ({p.weekday}) — every eligible subscription already has a delivery, or none are scheduled for this weekday.
              </div>
            ) : (
              <div className="max-h-56 overflow-y-auto rounded-md border border-border" data-testid="generate-dialog-candidates-list">
                {visibleCandidates.map((c) => (
                  <div key={c.subscription_id} className="flex items-center justify-between border-b border-border px-3 py-1.5 text-sm last:border-b-0">
                    <span className="truncate text-text-main">{c.customer_name || `Customer #${c.customer_id}`} — {c.plan_name}</span>
                    <span className="flex-shrink-0 text-xs text-text-muted">{c.deliveries_done}/{c.plan_quantity}</span>
                  </div>
                ))}
                {p.candidates.length > 50 && (
                  <p className="px-3 py-1.5 text-xs text-text-muted">+ {p.candidates.length - 50} more</p>
                )}
              </div>
            )}
          </>
        )}

        <DialogFooter>
          <Button variant="secondary" onClick={() => onOpenChange(false)} data-testid="generate-dialog-cancel-button">
            Cancel
          </Button>
          <Button
            variant="accent"
            disabled={generating || state.loading || !!state.error || !p?.will_create}
            onClick={handleConfirm}
            data-testid="generate-dialog-confirm-button"
          >
            {generating ? "Generating..." : p ? `Confirm & Generate ${p.will_create}` : "Confirm & Generate"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
